// ── Zone Activity rendering ───────────────────────────────────────────────
// Cards are built as one innerHTML string per render pass, then live updates
// patch only the four things that change per card (hero count, plate marks,
// roster, --za-live) and only on cards whose occupancy signature moved.

import { escapeHtml } from '../utils/html';
import { formatTimeDiff } from '../utils/time';
import { formatInSessionTz } from '../utils/timezone';
import type { PreparedZone, ZoneAgg, ZoneAnalysisResult, ZoneVisit, LiveOccupant, LiveOccupancy } from './zone-activity-types';
import type { ZoneActivityPageData } from './zone-activity-data';

const MARK_MAX = 12;

const el = (id: string): HTMLElement | null => document.getElementById(id);

export function showZoneActivityState(
  s: 'empty' | 'loading' | 'error' | 'results',
  msg = '',
): void {
  el('zaEmpty')  ?.classList.toggle('hidden', s !== 'empty');
  el('zaLoading')?.classList.toggle('hidden', s !== 'loading');
  el('zaErrBox') ?.classList.toggle('hidden', s !== 'error');
  el('zaResults')?.classList.toggle('hidden', s !== 'results');
  if (s === 'error' && msg) el('zaErrBox')!.textContent = msg;
  if (s === 'loading' && msg) el('zaLoadingLabel')!.textContent = msg;
}

// ── Formatting ────────────────────────────────────────────────────────────

export function formatDwell(ms: number): string {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return mins % 60 ? `${hrs}h ${mins % 60}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return hrs % 24 ? `${days}d ${hrs % 24}h` : `${days}d`;
}

/** Rendered on the user's MyGeotab profile clock — see utils/timezone.ts. */
export function formatStamp(ms: number): string {
  return formatInSessionTz(ms, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Occupancy magnitude bucket → plate rim weight, so "how busy" reads across cards. */
function rimWeight(count: number): number {
  if (count === 0) return 1;
  if (count <= 3)  return 1.4;
  if (count <= 9)  return 2;
  return 2.8;
}

// ── The plate ─────────────────────────────────────────────────────────────

/** One hidden SVG holding the stipple pattern every plate references. */
export function stippleSprite(): string {
  return `<svg width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false">
    <defs>
      <pattern id="zaStipple" width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="1.2" cy="1.2" r="0.5" fill="currentColor" opacity="0.5"/>
        <circle cx="3.7" cy="3.7" r="0.5" fill="currentColor" opacity="0.5"/>
      </pattern>
    </defs>
  </svg>`;
}

function markShape(o: LiveOccupant): string {
  const x = o.plateX.toFixed(1);
  const y = o.plateY.toFixed(1);
  if (o.isStale)   return `<circle class="za-mark za-mark-stale" cx="${x}" cy="${y}" r="2.4"/>`;
  if (o.isDriving) {
    // A rotated square: shape, not colour, distinguishes driving from parked.
    return `<rect class="za-mark za-mark-driving" x="${(o.plateX - 2.1).toFixed(1)}" y="${(o.plateY - 2.1).toFixed(1)}" width="4.2" height="4.2" transform="rotate(45 ${x} ${y})"/>`;
  }
  return `<circle class="za-mark za-mark-parked" cx="${x}" cy="${y}" r="2.5"/>`;
}

function marksHtml(occupants: LiveOccupant[]): string {
  return occupants.slice(0, MARK_MAX).map(markShape).join('');
}

/**
 * The engraved plate. Geometry lives once in <defs> with NO class, and every
 * visible stroke is a <use>: a class on the source path would win the cascade
 * over the inherited values and render the echoes filled.
 */
export function plateSvg(zone: PreparedZone, occupants: LiveOccupant[]): string {
  const pid = `zp-${zone.id}`;
  const cap = zone.plate.kind === 'circle' && zone.plate.radiusM > 0
    ? `<text class="za-plate-cap" x="50" y="97" text-anchor="middle">r ${zone.plate.radiusM}m</text>`
    : '';
  return `<svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <defs><path id="${escapeHtml(pid)}" d="${escapeHtml(zone.plate.d)}"/></defs>
    <use class="za-echo za-echo-3" href="#${escapeHtml(pid)}"/>
    <use class="za-echo za-echo-2" href="#${escapeHtml(pid)}"/>
    <use class="za-echo za-echo-1" href="#${escapeHtml(pid)}"/>
    <use class="za-rim" href="#${escapeHtml(pid)}"/>
    <g class="za-marks">${marksHtml(occupants)}</g>
    ${cap}
  </svg>`;
}

// ── Card ──────────────────────────────────────────────────────────────────
// Layout rule for everything below: each band is either a fixed-px grid track
// or the single flexible void, and each leaf is either a one-line ellipsis box
// or a line-clamp whose line-height is declared in PX. The previous card set
// `max-height:2.4em` on a wrapping flex roster, which is not an integer
// multiple of its own line box, so the second row rendered sliced through the
// glyphs. Budgets expressed in em over a `normal` line-height cannot be
// reconciled by tuning a number — hence the structural rule.

/** Occupancy split. Exact counts never truncate, which a name list always will. */
interface Tally { driving: number; parked: number; stale: number }

function tallyOf(occupants: LiveOccupant[]): Tally {
  const t: Tally = { driving: 0, parked: 0, stale: 0 };
  for (const o of occupants) {
    if (o.isStale) t.stale++;
    else if (o.isDriving) t.driving++;
    else t.parked++;
  }
  return t;
}

/**
 * Three fixed rows, always present and always in the same order, so the eye
 * learns "third row is stale" once and can then sample that row across a wall
 * of cards without reading. This replaces the old three-name roster: three
 * names out of sixty is a biased sample that also happened to be the thing
 * that clipped, and the detail modal already lists every vehicle.
 */
function tallyHtml(occupants: LiveOccupant[]): string {
  const t = tallyOf(occupants);
  const row = (cls: string, n: number, label: string): string =>
    `<li class="za-t-row ${cls}${n === 0 ? ' is-zero' : ''}"><b>${n}</b><span>${label}</span></li>`;
  return row('is-driving', t.driving, 'driving')
       + row('is-parked',  t.parked,  'parked')
       + row('is-stale',   t.stale,   'stale');
}

/** Occupancy magnitude → rail fill. Log-ish because occupancy is long-tailed. */
function fillLevel(count: number): number {
  if (count === 0)  return 0;
  if (count <= 2)   return 0.22;
  if (count <= 6)   return 0.42;
  if (count <= 15)  return 0.62;
  if (count <= 40)  return 0.82;
  return 1;
}

function ruleHtml(agg: ZoneAgg | undefined): string {
  if (!agg) {
    return Array.from({ length: 24 }, () => '<span class="za-tick"></span>').join('');
  }
  const peak = Math.max(...agg.byHour);
  return agg.byHour.map(v => {
    const h = peak > 0 ? Math.max(0.14, v / peak) : 0.14;
    const isPeak = peak > 0 && v === peak;
    return `<span class="za-tick${isPeak ? ' is-peak' : ''}" style="--h:${h.toFixed(2)}"></span>`;
  }).join('');
}

/**
 * One nowrap line spanning the full card width. The old version was a
 * four-column <dl> of uppercase labels in a ~188px column: "TOTAL DWELL" alone
 * measures ~64px, so the labels wrapped, the band grew, and `overflow:hidden`
 * silently sheared the card's bottom edge. One line cannot wrap.
 */
function footLineHtml(agg: ZoneAgg | undefined): string {
  if (!agg) return '<span class="za-foot-idle">No analysis in range</span>';
  const avg = agg.visitCount ? agg.totalDwellMs / agg.visitCount : 0;
  return `<b>${agg.visitCount.toLocaleString()}</b> visits`
       + ` · <b>${agg.byVehicle.size.toLocaleString()}</b> veh`
       + ` · <b>${escapeHtml(formatDwell(avg))}</b> avg`;
}

export interface CardContext {
  zone: PreparedZone;
  index: number;
  occupants: LiveOccupant[];
  agg: ZoneAgg | undefined;
  /** 0–1 visit percentile across the grid; drives the echo intensity. */
  heat: number;
}

/** The live half of the label. The history half is stored on the slot so a
 *  patch can reattach it without needing the ZoneAgg again. */
function ariaLive(zone: PreparedZone, occupants: LiveOccupant[]): string {
  const count = occupants.length;
  const t = tallyOf(occupants);
  const head = count > 0
    ? `${count} vehicle${count === 1 ? '' : 's'} inside now, ${t.driving} driving, ${t.parked} parked, ${t.stale} stale`
    : 'No vehicles inside';
  return `${zone.name}. ${head}.`;
}

function ariaHistory(agg: ZoneAgg | undefined): string {
  return agg ? ` ${agg.visitCount} visits by ${agg.byVehicle.size} vehicles in range.` : '';
}

export function cardHtml(ctx: CardContext): string {
  const { zone, index, occupants, agg, heat } = ctx;
  const count  = occupants.length;
  const hasStale = occupants.some(o => o.isStale);

  const cls = [
    'za-card',
    count > 0 ? 'is-occupied' : 'is-empty',
    agg ? 'has-history' : '',
    hasStale ? 'has-stale' : '',
  ].filter(Boolean).join(' ');

  const vars = `--za-live:${count > 0 ? 1 : 0}`
             + `;--za-heat:${heat.toFixed(2)}`
             + `;--za-rim-w:${rimWeight(count)}`
             + `;--za-fill:${fillLevel(count)}`;

  // Types are their own element so they ellipsize; the index is a separate
  // flex:none sibling. Previously `№ N` sat INSIDE the ellipsised kicker, so a
  // zone with three type names lost its index before it lost a type name.
  const types = zone.typeNames.length ? zone.typeNames.join(' · ') : 'Zone';
  const tag   = zone.ruleIds.length
    ? '<span class="za-tag is-verified" title="Visits for this zone are read from Geotab\'s own zone-stop records.">VER</span>'
    : '<span class="za-tag is-est" title="No zone-stop rule for this zone — visits are estimated from trip history.">EST</span>';

  const hist = ariaHistory(agg);

  return `<div class="za-slot" role="option" tabindex="-1" aria-selected="false" data-id="${escapeHtml(zone.id)}" data-hist="${escapeHtml(hist)}" aria-label="${escapeHtml(ariaLive(zone, occupants) + hist)}">
    <article class="${cls}" style="${vars}">
      <span class="za-rail"><i></i></span>
      <div class="za-body">
        <div class="za-meta">
          <span class="za-types">${escapeHtml(types)}</span>
          ${tag}
          <span class="za-idx">№ ${index}</span>
        </div>
        <h3 class="za-name" title="${escapeHtml(zone.name)}">${escapeHtml(zone.name)}</h3>
        <div class="za-main">
          <div class="za-heroblock">
            <div class="za-hero">${count > 0 ? count : '—'}</div>
            <div class="za-hero-cap"><span class="za-cap-txt">${count > 0 ? 'Inside now' : 'Clear'}</span><span class="za-pulse"></span></div>
          </div>
          <ul class="za-tally">${tallyHtml(occupants)}</ul>
        </div>
        <div class="za-plate">${plateSvg(zone, occupants)}</div>
        <div class="za-foot">
          <div class="za-rule" aria-hidden="true">${ruleHtml(agg)}</div>
          <div class="za-foot-line">${footLineHtml(agg)}</div>
        </div>
      </div>
    </article>
  </div>`;
}

/** Signature of a zone's live state — unchanged signature means zero DOM writes. */
export function liveSignature(occupants: LiveOccupant[]): string {
  return occupants.length + '|' + occupants
    .map(o => `${o.deviceId}${o.isDriving ? 'd' : ''}${o.isStale ? 's' : ''}`)
    .join(',');
}

/**
 * Patch one card in place from new live data. Touches only text, the marks
 * group, the tally and custom properties — never a layout property, so a
 * refresh can never reflow the grid. The pulse dot is a permanent node toggled
 * by CSS rather than re-created: rebuilding it restarted its 2.4s animation on
 * every tick, so the dots never settled into phase across the grid.
 */
export function patchCardLive(slot: HTMLElement, zone: PreparedZone, occupants: LiveOccupant[]): void {
  const card = slot.querySelector<HTMLElement>('.za-card');
  if (!card) return;
  const count = occupants.length;

  card.classList.toggle('is-occupied', count > 0);
  card.classList.toggle('is-empty', count === 0);
  card.classList.toggle('has-stale', occupants.some(o => o.isStale));
  card.style.setProperty('--za-live', count > 0 ? '1' : '0');
  card.style.setProperty('--za-rim-w', String(rimWeight(count)));
  card.style.setProperty('--za-fill', String(fillLevel(count)));

  const hero = card.querySelector<HTMLElement>('.za-hero');
  if (hero) hero.textContent = count > 0 ? String(count) : '—';

  const capTxt = card.querySelector<HTMLElement>('.za-cap-txt');
  if (capTxt) capTxt.textContent = count > 0 ? 'Inside now' : 'Clear';

  const marks = card.querySelector<SVGGElement>('.za-marks');
  if (marks) marks.innerHTML = marksHtml(occupants);

  const tally = card.querySelector<HTMLElement>('.za-tally');
  if (tally) tally.innerHTML = tallyHtml(occupants);

  // setAttribute takes a raw string — escaping here would surface literal &amp;
  // to a screen reader. The history half is reattached from the slot.
  slot.setAttribute('aria-label', ariaLive(zone, occupants) + (slot.dataset.hist ?? ''));
}

// ── Detail modal ──────────────────────────────────────────────────────────

export interface DetailSort {
  col: 'name' | 'visits' | 'dwell' | 'first' | 'last';
  asc: boolean;
}

/** Sort applied to every expanded entry/exit list at once (there is no per-row independent sort). */
export interface VisitSort {
  col: 'entry' | 'exit' | 'dwell' | 'driver';
  asc: boolean;
}

const VISIT_COLS: Array<{ key: VisitSort['col']; label: string }> = [
  { key: 'entry',  label: 'Entry' },
  { key: 'exit',   label: 'Exit' },
  { key: 'dwell',  label: 'Dwell' },
  { key: 'driver', label: 'Driver' },
];

function sortVisits(visits: ZoneVisit[], sort: VisitSort, data: ZoneActivityPageData): ZoneVisit[] {
  const driverName = (v: ZoneVisit): string => v.driverId ? (data.userMap.get(v.driverId) ?? v.driverId) : 'Unassigned';
  const sorted = [...visits];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (sort.col) {
      case 'exit':   cmp = (a.exitMs ?? Infinity) - (b.exitMs ?? Infinity); break;
      case 'dwell':  cmp = a.dwellMs - b.dwellMs; break;
      case 'driver': cmp = driverName(a).localeCompare(driverName(b)); break;
      default:       cmp = a.entryMs - b.entryMs;
    }
    return sort.asc ? cmp : -cmp;
  });
  return sorted;
}

function visitRowsHtml(visits: ZoneVisit[], sort: VisitSort, data: ZoneActivityPageData): string {
  const head = VISIT_COLS.map(c => {
    const active = sort.col === c.key;
    const arrow  = active ? (sort.asc ? ' ▲' : ' ▼') : '';
    return `<th class="${active ? 'sort-active' : ''}" data-vsort="${c.key}">${escapeHtml(c.label)}${arrow}</th>`;
  }).join('');

  const rows = sortVisits(visits, sort, data).map(visit => `<tr>
      <td class="td-mono">${escapeHtml(formatStamp(visit.entryMs))}${visit.alreadyInsideAtStart ? ' <span class="za-badge za-badge-prior" title="This visit was already in progress when the selected range began — the entry shown is its true arrival time.">before range</span>' : ''}</td>
      <td class="td-mono">${visit.exitMs === null ? '<span class="za-badge za-badge-inside">still inside</span>' : escapeHtml(formatStamp(visit.exitMs))}</td>
      <td class="td-mono">${escapeHtml(formatDwell(visit.dwellMs))}</td>
      <td class="td-muted">${escapeHtml(visit.driverId ? (data.userMap.get(visit.driverId) ?? visit.driverId) : 'Unassigned')}</td>
    </tr>`).join('');

  return `<table class="za-visits-tbl"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

export function renderZoneDetail(
  zone: PreparedZone,
  occupants: LiveOccupant[],
  agg: ZoneAgg | undefined,
  data: ZoneActivityPageData,
  sort: DetailSort,
  innerSort: VisitSort,
): void {
  el('zaDetailKicker')!.textContent = zone.typeNames.length ? zone.typeNames.join(' · ') : 'Zone';
  el('zaDetailName')!.textContent   = zone.name;

  const accuracyEl = el('zaDetailAccuracy');
  if (accuracyEl) {
    accuracyEl.innerHTML = zone.ruleIds.length
      ? '<span class="za-badge za-badge-verified" title="A Geotab Rule already targets this zone — visits below are read directly from its ExceptionEvents, matching Geotab\'s own reports exactly.">verified</span>'
      : '<span class="za-badge za-badge-estimated" title="No Geotab Rule targets this zone yet — visits below are estimated from Trip stop locations, not read from a rule.">estimated</span>';
  }

  const commentEl = el('zaDetailComment')!;
  commentEl.textContent = zone.comment;
  commentEl.classList.toggle('hidden', !zone.comment);

  el('zaDetailPlate')!.innerHTML = plateSvg(zone, occupants);

  const parts: string[] = [];

  // KPI strip
  const kpis: Array<[string, string]> = [['Inside now', String(occupants.length)]];
  if (agg) {
    kpis.push(
      ['Visits', String(agg.visitCount)],
      ['Vehicles', String(agg.byVehicle.size)],
      ['Total dwell', formatDwell(agg.totalDwellMs)],
    );
  }
  parts.push(`<div class="kpi-grid">${kpis.map(([label, val]) =>
    `<div class="kpi-card"><div class="kpi-val">${escapeHtml(val)}</div><div class="kpi-lbl">${escapeHtml(label)}</div></div>`,
  ).join('')}</div>`);

  // Live occupants
  parts.push('<div class="za-detail-section-title">Currently inside</div>');
  if (!occupants.length) {
    parts.push('<div class="za-detail-hint">No vehicles are inside this zone right now.</div>');
  } else {
    parts.push(`<div class="za-inside-list">${occupants.map(o => {
      const age = o.isStale && o.lastCommMs
        ? `<span class="za-tag-age">${escapeHtml(formatTimeDiff(new Date(o.lastCommMs)))}</span>`
        : '';
      const driver = o.driverName
        ? `<span class="za-tag-driver">· ${escapeHtml(o.driverName)}</span>`
        : `<span class="za-tag-driver">· ${o.isDriving ? 'driving' : 'parked'}</span>`;
      return `<span class="za-inside-tag">${escapeHtml(o.deviceName)}${driver}${age}</span>`;
    }).join('')}</div>`);
  }

  // Historical table
  parts.push('<div class="za-detail-section-title">Visit history</div>');
  if (!agg) {
    parts.push('<div class="za-detail-hint">Pick a date range and click <strong>Run Analysis</strong> to see who visited this zone and when.</div>');
  } else {
    parts.push(vehicleTableHtml(agg, data, sort, innerSort));
  }

  el('zaDetailBody')!.innerHTML = parts.join('');
}

const DETAIL_COLS: Array<{ key: DetailSort['col']; label: string }> = [
  { key: 'name',   label: 'Vehicle' },
  { key: 'visits', label: 'Visits' },
  { key: 'dwell',  label: 'Total dwell' },
  { key: 'first',  label: 'First entry' },
  { key: 'last',   label: 'Last exit' },
];

function vehicleTableHtml(agg: ZoneAgg, data: ZoneActivityPageData, sort: DetailSort, innerSort: VisitSort): string {
  const rows = [...agg.byVehicle.values()];
  const nameOf = (id: string): string => data.deviceNames.get(id) ?? id;

  rows.sort((a, b) => {
    let cmp = 0;
    switch (sort.col) {
      case 'visits': cmp = a.visits.length - b.visits.length; break;
      case 'dwell':  cmp = a.totalDwellMs - b.totalDwellMs; break;
      case 'first':  cmp = a.firstEntryMs - b.firstEntryMs; break;
      case 'last':   cmp = (a.lastExitMs ?? Infinity) - (b.lastExitMs ?? Infinity); break;
      default:       cmp = nameOf(a.deviceId).localeCompare(nameOf(b.deviceId));
    }
    return sort.asc ? cmp : -cmp;
  });

  const head = DETAIL_COLS.map(c => {
    const active = sort.col === c.key;
    const arrow  = active ? (sort.asc ? ' ▲' : ' ▼') : '';
    return `<th class="${active ? 'sort-active' : ''}" data-sort="${c.key}">${escapeHtml(c.label)}${arrow}</th>`;
  }).join('');

  const body = rows.map(v => `<tr class="za-vrow" data-dev="${escapeHtml(v.deviceId)}">
        <td><span class="za-chev">▸</span> ${escapeHtml(nameOf(v.deviceId))}${v.stillInside ? ' <span class="za-badge za-badge-inside">inside at range end</span>' : ''}</td>
        <td class="td-mono">${v.visits.length}</td>
        <td class="td-mono">${escapeHtml(formatDwell(v.totalDwellMs))}</td>
        <td class="td-mono td-muted">${escapeHtml(formatStamp(v.firstEntryMs))}</td>
        <td class="td-mono td-muted">${v.lastExitMs === null ? '—' : escapeHtml(formatStamp(v.lastExitMs))}</td>
      </tr>
      <tr class="za-visits-row hidden" data-visits="${escapeHtml(v.deviceId)}">
        <td class="za-visits-cell" colspan="5">
          <div class="za-visits-inner">${visitRowsHtml(v.visits, innerSort, data)}</div>
        </td>
      </tr>`).join('');

  return `<table class="za-vtable" id="zaDetailTable">
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>`;
}

/** Visit-count percentile per zone, used for the echo intensity. */
export function buildHeatMap(result: ZoneAnalysisResult | null): Map<string, number> {
  const heat = new Map<string, number>();
  if (!result) return heat;
  const counts = [...result.byZone.values()].map(a => a.visitCount).sort((a, b) => a - b);
  if (!counts.length) return heat;
  const max = counts[counts.length - 1];
  result.byZone.forEach((agg, zoneId) => {
    heat.set(zoneId, max > 0 ? agg.visitCount / max : 0);
  });
  return heat;
}

// ── Single-vehicle / single-driver view ─────────────────────────────────────
// The zone-by-zone grid answers "who was in this zone" — this is the inverse:
// "where did this one vehicle/driver go". It reads the SAME already-computed
// ZoneAnalysisResult (no separate fetch), just sliced down to one entity's
// visits and regrouped by zone instead of by vehicle.

export interface EntityZoneRow {
  zoneId: string;
  zoneName: string;
  typeNames: string[];
  visits: ZoneVisit[];
  totalDwellMs: number;
  firstEntryMs: number;
  lastExitMs: number | null;
  stillInside: boolean;
  /** This zone has a matching Geotab Rule — its visits are exact, not estimated. */
  ruleBacked: boolean;
}

/** Every zone a single vehicle or driver touched, built by filtering the already-computed result. */
export function buildEntityZoneRows(
  result: ZoneAnalysisResult,
  data: ZoneActivityPageData,
  matches: (visit: ZoneVisit) => boolean,
): EntityZoneRow[] {
  const zoneById = new Map(data.zones.map(z => [z.id, z]));
  const rows: EntityZoneRow[] = [];

  result.byZone.forEach((agg, zoneId) => {
    const visits: ZoneVisit[] = [];
    agg.byVehicle.forEach(veh => veh.visits.forEach(v => { if (matches(v)) visits.push(v); }));
    if (!visits.length) return;

    visits.sort((a, b) => a.entryMs - b.entryMs);
    const zone = zoneById.get(zoneId);
    let totalDwellMs = 0, lastExitMs: number | null = null, stillInside = false;
    visits.forEach(v => {
      totalDwellMs += v.dwellMs;
      if (v.stillInsideAtEnd) stillInside = true;
      if (v.exitMs !== null && (lastExitMs === null || v.exitMs > lastExitMs)) lastExitMs = v.exitMs;
    });

    rows.push({
      zoneId,
      zoneName: zone?.name ?? zoneId,
      typeNames: zone?.typeNames ?? [],
      visits, totalDwellMs, lastExitMs, stillInside,
      firstEntryMs: visits[0].entryMs,
      ruleBacked: (zone?.ruleIds.length ?? 0) > 0,
    });
  });

  rows.sort((a, b) => b.visits.length - a.visits.length || a.zoneName.localeCompare(b.zoneName));
  return rows;
}

export interface EntitySort {
  col: 'zone' | 'visits' | 'dwell' | 'first' | 'last';
  asc: boolean;
}

const ENTITY_COLS: Array<{ key: EntitySort['col']; label: string }> = [
  { key: 'zone',   label: 'Zone' },
  { key: 'visits', label: 'Visits' },
  { key: 'dwell',  label: 'Total dwell' },
  { key: 'first',  label: 'First entry' },
  { key: 'last',   label: 'Last exit' },
];

/** Renders the KPI strip + expandable zone table for a single vehicle/driver into `containerId`. */
export function renderEntityResults(
  containerId: string,
  rows: EntityZoneRow[],
  data: ZoneActivityPageData,
  sort: EntitySort,
  innerSort: VisitSort,
): void {
  const container = el(containerId);
  if (!container) return;

  if (!rows.length) {
    container.innerHTML = '<div class="za-detail-hint">No zone visits found for this selection in the chosen range.</div>';
    return;
  }

  let totalVisits = 0, totalDwellMs = 0;
  rows.forEach(r => {
    totalDwellMs += r.totalDwellMs;
    totalVisits += r.visits.length;
  });

  const kpis: Array<[string, string]> = [
    ['Zones visited', String(rows.length)],
    ['Visits', String(totalVisits)],
    ['Total dwell', formatDwell(totalDwellMs)],
  ];

  const sorted = [...rows];
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (sort.col) {
      case 'visits': cmp = a.visits.length - b.visits.length; break;
      case 'dwell':  cmp = a.totalDwellMs - b.totalDwellMs; break;
      case 'first':  cmp = a.firstEntryMs - b.firstEntryMs; break;
      case 'last':   cmp = (a.lastExitMs ?? Infinity) - (b.lastExitMs ?? Infinity); break;
      default:       cmp = a.zoneName.localeCompare(b.zoneName);
    }
    return sort.asc ? cmp : -cmp;
  });

  const head = ENTITY_COLS.map(c => {
    const active = sort.col === c.key;
    const arrow  = active ? (sort.asc ? ' ▲' : ' ▼') : '';
    return `<th class="${active ? 'sort-active' : ''}" data-esort="${c.key}">${escapeHtml(c.label)}${arrow}</th>`;
  }).join('');

  const body = sorted.map(r => `<tr class="za-vrow" data-ezone="${escapeHtml(r.zoneId)}">
        <td>
          <span class="za-chev">▸</span> ${escapeHtml(r.zoneName)}
          ${r.typeNames.length ? `<span class="td-muted"> · ${escapeHtml(r.typeNames.join(', '))}</span>` : ''}
          ${r.ruleBacked
            ? ' <span class="za-badge za-badge-verified" title="A Geotab Rule already targets this zone — these visits are read directly from its ExceptionEvents, matching Geotab\'s own reports exactly.">verified</span>'
            : ' <span class="za-badge za-badge-estimated" title="No Geotab Rule targets this zone yet — these visits are estimated from Trip stop locations, not read from a rule.">estimated</span>'}
          ${r.stillInside ? ' <span class="za-badge za-badge-inside">inside at range end</span>' : ''}
        </td>
        <td class="td-mono">${r.visits.length}</td>
        <td class="td-mono">${escapeHtml(formatDwell(r.totalDwellMs))}</td>
        <td class="td-mono td-muted">${escapeHtml(formatStamp(r.firstEntryMs))}</td>
        <td class="td-mono td-muted">${r.lastExitMs === null ? '—' : escapeHtml(formatStamp(r.lastExitMs))}</td>
      </tr>
      <tr class="za-visits-row hidden" data-evisits="${escapeHtml(r.zoneId)}">
        <td class="za-visits-cell" colspan="5">
          <div class="za-visits-inner">${visitRowsHtml(r.visits, innerSort, data)}</div>
        </td>
      </tr>`).join('');

  container.innerHTML = `
    <div class="kpi-grid">${kpis.map(([label, val]) =>
      `<div class="kpi-card"><div class="kpi-val">${escapeHtml(val)}</div><div class="kpi-lbl">${escapeHtml(label)}</div></div>`,
    ).join('')}</div>
    <table class="za-vtable" id="zaEntityTable">
      <thead><tr>${head}</tr></thead>
      <tbody>${body}</tbody>
    </table>`;
}
